import React, { useState, useEffect } from "react";
import { Badge, EmptyState as Empty } from "../../../../components/common";
import { FilterSection, AdvancedFilters as ActivityAdvancedFilters, StatCard, Pagination, DataTable } from "../../../../components/ui";
import { Clock, Coffee, MapPin, Search, Grid, List as ListIcon, X, CheckCircle2, Eye, RefreshCw, Calendar, Download } from "lucide-react";
import api from "../../../../services/api";
import { useAuthStore as useApp } from "../../../../store/authStore";
import toast from "react-hot-toast";
import { AttendanceMapModal } from "./components/AttendanceMapModal";

// Helper to format date cleanly like "Apr 12, 2026"
const fmtDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const AttendanceTrackerPage = () => {
    const { user } = useApp();
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("table");
    const [search, setSearch] = useState("");
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });

    // Advanced Filters State
    const [filters, setFilters] = useState({
        status: 'all',
        role: 'all',
        fromDate: '',
        toDate: '',
        date: 'all' // all, today, this_week, this_month, this_year
    });

    const handleFilterChange = (keyOrObj, value) => {
        if (typeof keyOrObj === 'object') {
            setFilters(prev => ({ ...prev, ...keyOrObj }));
        } else {
            setFilters(prev => ({ ...prev, [keyOrObj]: value }));
        }
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Tracking Modal State
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isMapOpen, setIsMapOpen] = useState(false);

    const openTracking = (record) => {
        setSelectedRecord(record);
        setIsMapOpen(true);
    };

    useEffect(() => {
        fetchRecords();
    }, [filters, pagination.page, pagination.limit]);

    const [serverStats, setServerStats] = useState(null);

    const fetchRecords = async () => {
        setLoading(true);
        try {
            const query = new URLSearchParams();
            query.append('page', pagination.page);
            query.append('limit', pagination.limit);
            if (filters.status !== 'all') query.append('status', filters.status);
            if (filters.role !== 'all') query.append('role', filters.role);
            if (filters.fromDate) query.append('fromDate', filters.fromDate);
            if (filters.toDate) query.append('toDate', filters.toDate);
            
            if (!filters.fromDate && !filters.toDate && filters.date && filters.date !== 'all') {
                if (filters.date === 'today') query.append('range', 'today');
                else if (filters.date === 'this_week') query.append('range', 'week');
                else if (filters.date === 'this_month') query.append('range', 'month');
                else if (filters.date === 'this_year') query.append('range', 'year');
            }

            const res = await api.get(`/attendance?${query.toString()}`);


            // Handle various response structures defensively
            let fetchedRecords = [];
            let fetchedPagination = { page: 1, limit: 50, total: 0, totalPages: 0 };
            
            if (res && res.data && res.data.data && Array.isArray(res.data.data.records)) {
                fetchedRecords = res.data.data.records;
                if (res.data.data.pagination) fetchedPagination = res.data.data.pagination;
                if (res.data.data.stats) setServerStats(res.data.data.stats);
            } else if (res && res.data && Array.isArray(res.data.records)) {
                fetchedRecords = res.data.records;
                if (res.data.pagination) fetchedPagination = res.data.pagination;
                if (res.data.stats) setServerStats(res.data.stats);
            } else if (res && Array.isArray(res.records)) {
                fetchedRecords = res.records;
                if (res.pagination) fetchedPagination = res.pagination;
                if (res.stats) setServerStats(res.stats);
            } else if (Array.isArray(res)) {
                fetchedRecords = res;
            }

            setRecords(fetchedRecords);
            if (fetchedPagination.total !== undefined) {
                setPagination(prev => ({ ...prev, ...fetchedPagination }));
            }
        } catch (error) {
            console.error("Failed to load attendance", error);
        } finally {
            setLoading(false);
        }
    };

    const stats = serverStats || {
        total: records.length,
        active: records.filter(r => r.status === 'checked_in').length,
        onBreak: records.filter(r => r.status === 'on_break').length,
        completed: records.filter(r => r.status === 'checked_out').length,
    };

    const filtered = records.filter(r =>
        r.user?.name?.toLowerCase().includes(search.toLowerCase()) ||
        r.user?.employeeId?.toLowerCase().includes(search.toLowerCase())
    );

    const formatTime = (dateString) => {
        if (!dateString) return '--';
        return new Date(dateString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    };

    const formatLongDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const handleExport = () => {
        if (filtered.length === 0) return toast.error("No tracking data to export");
        const headers = ["Employee", "ID", "Role", "Status", "Check-In", "Check-Out", "Total Break (mins)", "Net Hours", "Gross Hours"];
        const csvContent = [
            headers.join(","),
            ...filtered.map(r => [
                `"${r.user?.name || ''}"`,
                `"${r.user?.employeeId || ''}"`,
                `"${r.user?.role || ''}"`,
                `"${r.status === 'checked_in' ? 'Active' : r.status === 'on_break' ? 'On Break' : 'Off-Duty'}"`,
                `"${formatTime(r.checkIn)}"`,
                `"${formatTime(r.checkOut)}"`,
                r.totalBreakMinutes || 0,
                r.totalHours || 0,
                r.grossHours || 0
            ].join(","))
        ].join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `attendance_tracker_${new Date().toISOString().split('T')[0]}.csv`);
        link.click();
        document.body.removeChild(link);
    };

    const columns = [
        {
            header: "Employee",
            minWidth: "200px",
            render: (r) => (
                <div className="flex flex-col">
                    <div className="font-semibold text-brand-dark text-[13px]">{r.user?.name}</div>
                    <div className="text-[13px] font-medium text-slate-400 mt-0.5">{r.user?.employeeId} • {r.user?.role}</div>
                </div>
            )
        },
        {
            header: "Date",
            minWidth: "120px",
            render: (r) => (
                <span className="font-medium text-slate-900 text-[13px] whitespace-nowrap">{formatLongDate(r.date || r.createdAt)}</span>
            )
        },
        {
            header: "Status",
            minWidth: "100px",
            render: (r) => (
                <div className="flex">
                    {r.status === 'checked_in' && <span className="px-2.5 py-1 rounded-full text-[13px] font-medium bg-emerald-100 text-emerald-700 whitespace-nowrap">Active</span>}
                    {r.status === 'on_break' && <span className="px-2.5 py-1 rounded-full text-[13px] font-medium bg-amber-100 text-amber-700 whitespace-nowrap">On Break</span>}
                    {r.status === 'checked_out' && <span className="px-2.5 py-1 rounded-full text-[13px] font-medium bg-slate-100 text-slate-600 whitespace-nowrap">Off-Duty</span>}
                </div>
            )
        },
        {
            header: "Check-In",
            minWidth: "120px",
            render: (r) => (
                <span className="font-medium text-slate-900 text-[13px] whitespace-nowrap">{formatTime(r.checkIn)}</span>
            )
        },
        {
            header: "Total Break",
            minWidth: "120px",
            render: (r) => (
                <span className="text-slate-500 text-[13px] whitespace-nowrap">{r.totalBreakMinutes || 0} min</span>
            )
        },
        {
            header: "Check-Out",
            minWidth: "120px",
            render: (r) => (
                <span className="font-medium text-slate-900 text-[13px] whitespace-nowrap">{formatTime(r.checkOut)}</span>
            )
        },
        {
            header: "Net Hours",
            minWidth: "120px",
            render: (r) => (
                <div className="flex flex-col whitespace-nowrap">
                    <span className="font-medium text-brand-blue text-[13px]">{r.totalHours ? `${r.totalHours} hrs` : '--'}</span>
                    {r.grossHours ? <span className="text-[11px] text-slate-400">Total: {r.grossHours}h</span> : null}
                </div>
            )
        },
        {
            header: "Actions",
            minWidth: "100px",
            cellClassName: "!text-right",
            headerClassName: "text-right",
            render: (r) => (
                <div className="flex justify-end">
                    <button
                        onClick={() => openTracking(r)}
                        className="p-2 text-brand-blue hover:bg-blue-50 rounded-lg transition-colors group"
                        title="View GPS Tracking Path"
                    >
                        <Eye size={18} className="transition-transform" />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="space-y-5">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div>
                    <h2 className="text-[13px] font-semibold text-brand-dark mb-1">Attendance Tracker</h2>
                    <p className="text-[13px] font-medium text-slate-500">Monitor employee field shifts, breaks, and native GPS punch locations</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 auto-rows-fr">
                <StatCard variant="dashboard" title="Total Staff Active" value={stats.total} />
                <StatCard variant="dashboard" title="Currently Tracking" value={stats.active} />
                <StatCard variant="dashboard" title="Currently on Break" value={stats.onBreak} />
                <StatCard variant="dashboard" title="Shifts Completed" value={stats.completed} />
            </div>

            <FilterSection
                search={search}
                setSearch={setSearch}
                filters={filters}
                setFilters={handleFilterChange}
                viewStyle={view}
                setViewStyle={setView}
                statusOptions={[
                    { value: "checked_in", label: "Checked In" },
                    { value: "on_break", label: "On Break" },
                    { value: "checked_out", label: "Checked Out" }
                ]}
                statusPlaceholder="All Statuses"
                onExport={handleExport}
            >
                <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0">
                    <select
                        value={filters.role}
                        onChange={(e) => handleFilterChange("role", e.target.value)}
                        className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-blue text-slate-600 min-w-[140px] max-w-[180px] truncate w-auto"
                    >
                        <option value="all">All roles</option>
                        <option value="sales-exec">Sales Executive</option>
                        <option value="field_officer">Field Officer</option>
                    </select>
                </div>
            </FilterSection>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{[1, 2, 3].map(n => <div key={n} className="card h-48 animate-pulse bg-slate-50" />)}</div>
            ) : filtered.length === 0 ? (
                <div className="py-12"><Empty title="No tracking data" desc="No attendance records found for this period." icon="📍" /></div>
            ) : view === "table" ? (
                <div className="flex flex-col bg-white rounded-xl shadow-sm border border-slate-200">
                    <DataTable 
                        columns={columns} 
                        data={filtered} 
                        noScroll={true}
                    />
                    {pagination.totalPages > 1 && (
                        <div className="p-4 border-t border-slate-200 bg-white">
                            <Pagination pagination={pagination} setPagination={setPagination} />
                        </div>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Kanban Board style grouped by Status */}
                    {['checked_in', 'on_break', 'checked_out'].map(statusLayer => {
                        const items = filtered.filter(f => f.status === statusLayer);
                        const names = { checked_in: "Active in Field", on_break: "Currently on Break", checked_out: "Shift Completed" };

                        return (
                            <div key={statusLayer} className="bg-slate-50 p-4 rounded-2xl border border-slate-100 h-full">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-semibold text-brand-dark text-[15px]">{names[statusLayer]}</h3>
                                    <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-xs font-bold">{items.length}</div>
                                </div>
                                <div className="space-y-3">
                                    {items.map(r => (
                                        <div key={r._id} className="p-3 border border-slate-100 hover:bg-slate-50 transition-all cursor-pointer rounded-lg bg-white">
                                                <div className="font-semibold text-[13px] text-brand-dark">{r.user?.name}</div>
                                                <div className="text-[13px] font-medium text-slate-400 mb-2">{r.user?.employeeId}</div>
                                                <div className="flex justify-between items-center bg-slate-50 px-2 py-1.5 rounded text-[13px] font-medium text-slate-600">
                                                    <span>In: {formatTime(r.checkIn)}</span>
                                                    {r.status === 'checked_out' && <span>Out: {formatTime(r.checkOut)}</span>}
                                                    {r.status !== 'checked_out' && <span className="flex items-center gap-1 text-emerald-600"><MapPin size={18} /> Tracking</span>}
                                                </div>
                                                <div className="mt-3 pt-2 border-t border-slate-50 flex justify-end">
                                                    <button
                                                        onClick={() => openTracking(r)}
                                                        className="flex items-center gap-1.5 text-[13px] font-medium text-brand-blue hover:text-blue-700 transition-colors normal-case"
                                                    >
                                                        <Eye size={18} /> View Path
                                                    </button>
                                                </div>
                                        </div>
                                    ))}
                                    {items.length === 0 && <div className="text-center py-6 text-sm text-slate-400">No employees here</div>}
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
            
            {view !== "table" && pagination.totalPages > 1 && (
                <div className="p-4 border border-slate-100 bg-white rounded-lg mt-4">
                    <Pagination pagination={pagination} setPagination={setPagination} />
                </div>
            )}

            <AttendanceMapModal
                isOpen={isMapOpen}
                onClose={() => setIsMapOpen(false)}
                attendance={selectedRecord}
            />
        </div>
    );
};
